import { useEffect } from 'react';
import type { Task } from '../../types';
import type { Language } from '../../i18n';
import { useTranslation } from '../../hooks/useTranslation';
import ComingSoon from '../ComingSoon';

interface TasksSectionProps {
  tasks: Task[];
  allTasks?: Task[]; // All tasks for accurate counting
  taskCategory: 'special' | 'social' | 'trading';
  language: Language;
  formatPoints: (points: number) => string;
  onCategoryChange: (category: 'special' | 'social' | 'trading') => void;
  onClaimTask: (taskId: string) => Promise<void>;
  onTaskStarted?: (task: Task, delayMinutes: number) => void;
  onLoadMore?: () => void;
  loading?: boolean;
}

const TasksSection = ({
  tasks,
  allTasks = [],
  taskCategory,
  language,
  formatPoints,
  onCategoryChange,
  onClaimTask,
  onTaskStarted,
  onLoadMore,
  loading = false,
}: TasksSectionProps) => {
  // ✅ IMPORTANT: Hooks MUST be called before any return statement!
  const { t } = useTranslation();
  
  // Use allTasks for counting if available, otherwise use tasks
  const tasksForCount = allTasks.length > 0 ? allTasks : tasks;

  // Infinite scroll observer - MUST be before any return statement
  useEffect(() => {
    if (!onLoadMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && onLoadMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const sentinel = document.getElementById('tasks-scroll-sentinel');
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [onLoadMore, loading]);
  
  // Show "Coming soon" for trading tasks
  if (taskCategory === 'trading') {
    return (
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white mb-2">
          {t('checkIn.tasks')}
        </h2>
        <p className="text-sm text-tg-hint mb-4">
          {t('checkIn.subtitle')}
        </p>
        
        {/* Task Category Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          <button
            onClick={() => onCategoryChange('special')}
            className="px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 bg-tg-secondary text-tg-hint border border-gray-800 hover:border-primary/50 hover:bg-[#2a2a2a] hover:scale-105"
          >
          {t('checkIn.special')}
        </button>
        <button
          onClick={() => onCategoryChange('social')}
          className="px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 bg-tg-secondary text-tg-hint border border-gray-800 hover:border-primary/50 hover:bg-[#2a2a2a] hover:scale-105"
        >
          {t('checkIn.social')}
        </button>
        <button
          onClick={() => onCategoryChange('trading')}
          className="px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 bg-primary text-black shadow-lg shadow-primary/30"
        >
          {t('checkIn.trading')}
        </button>
        </div>

        <ComingSoon 
          language={language}
          title={`🚧 ${t('checkIn.comingSoon')}`}
          message={t('common.loading')}
        />
      </div>
    );
  }

  const getActionTypeCode = (actionType: Task['actionType']): string | null => {
    if (!actionType) return null;
    if (typeof actionType === 'string') return actionType;
    if (typeof actionType === 'object' && 'code' in actionType) return actionType.code;
    return null;
  };

  // Filter tasks by category (backend already sorted them)
  const categoryTasks = tasks.filter(t => t.category === taskCategory);
  
  // Group tasks by exchange
  const tasksByExchange = categoryTasks.reduce((acc, task) => {
    let exchange = 'other';
    if (task.exchange) {
      if (typeof task.exchange === 'object' && task.exchange !== null) {
        exchange = (task.exchange as any).code || (task.exchange as any).id || 'other';
      } else {
        exchange = task.exchange as string || 'other';
      }
    }
    if (!acc[exchange]) acc[exchange] = [];
    acc[exchange].push(task);
    return acc;
  }, {} as Record<string, Task[]>);

  const getActionButtonText = (task: Task) => {
    if (task.status === 'claimed') {
      return t('checkIn.checkedIn');
    }
    // If processing and can claim, show "Claim" instead of "Processing..."
    if (task.status === 'processing' && task.canClaimAt) {
      const now = new Date();
      const canClaim = new Date(task.canClaimAt);
      if (now >= canClaim) {
        return t('checkIn.checkIn');
      }
    }
    if (task.status === 'in_progress') {
      return t('checkIn.checkIn');
    }
    if (task.category === 'social') {
      return t('common.next');
    }
    const actionCode = getActionTypeCode(task.actionType);
    if (task.category === 'special' && actionCode === 'connect_wallet') {
      return t('checkIn.checkIn');
    }
    if (task.category === 'special' && actionCode === 'watch_ad') {
      return t('checkIn.watchAd');
    }
    if (task.category === 'trading') {
      return t('checkIn.checkIn');
    }
    return t('common.next');
  };

  const getActionIcon = (task: Task) => {
    // Use task.icon if available (from admin)
    if (task.icon && task.icon.trim()) {
      return task.icon;
    }
    // Final fallback
    return '📋';
  };

  const handleTaskAction = async (task: Task) => {
    console.log('handleTaskAction called:', {
      id: task.id,
      category: task.category,
      actionUrl: task.actionUrl,
      actionType: task.actionType,
      status: task.status
    });

    // If task is processing and can be claimed, claim it
    if (task.status === 'processing' && task.canClaimAt) {
      const now = new Date();
      const canClaim = new Date(task.canClaimAt);
      if (now >= canClaim) {
        onClaimTask(task.id);
      }
      return;
    }

    // If task is already claimed, do nothing
    if (task.status === 'claimed') {
      return;
    }

    // For social tasks with actionUrl, open link FIRST then start task
    if (task.category === 'social' && task.actionUrl && (task.status === 'available' || !task.status)) {
      const url = task.actionUrl;
      const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
      
      // Check if it's a Telegram link (t.me, telegram.me, telegram.org)
      const isTelegramLink = url.includes('t.me/') || url.includes('telegram.me/') || url.includes('telegram.org/');
      
      // Open link directly using Telegram WebApp API (like in the sample code)
      // Try multiple approaches to ensure it works
      if (tg) {
        try {
          if (isTelegramLink && tg.openTelegramLink) {
            console.log('Opening Telegram link:', url);
            tg.openTelegramLink(url);
          } else if (tg.openLink) {
            console.log('Opening link with openLink:', url);
            // Try with and without options
            try {
              tg.openLink(url);
            } catch (e1) {
              try {
                tg.openLink(url, { try_instant_view: false });
              } catch (e2) {
                console.error('Both openLink attempts failed:', e1, e2);
                // Fallback: create anchor and click
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            }
          } else {
            console.warn('Telegram WebApp API not available');
            // Fallback
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        } catch (error) {
          console.error('Error opening link:', error);
          // Final fallback
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      } else {
        console.warn('Telegram WebApp not available, using window.open');
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      
      // Wait a bit to ensure link opens, then start the task (set to processing)
      setTimeout(async () => {
        await onClaimTask(task.id);
        // Show notification about delay if callback provided
        if (onTaskStarted) {
          const delayMinutes = task.delayMinutes || 30;
          onTaskStarted(task, delayMinutes);
        }
      }, 500);
      return;
    }

    // For special tasks with watch_ad action
    const actionCode = getActionTypeCode(task.actionType);
    if (task.category === 'special' && actionCode === 'watch_ad' && task.actionUrl && (task.status === 'available' || !task.status)) {
      const url = task.actionUrl;
      const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
      
      // Check if it's a Telegram link
      const isTelegramLink = url.includes('t.me/') || url.includes('telegram.me/') || url.includes('telegram.org/');
      
      // Open link directly using Telegram WebApp API (like in the sample code)
      if (tg) {
        if (isTelegramLink && tg.openTelegramLink) {
          tg.openTelegramLink(url);
        } else if (tg.openLink) {
          tg.openLink(url); // No options, just like in the sample code
        }
      }
      
      // Wait a bit then start the task
      setTimeout(() => {
        onClaimTask(task.id);
      }, 500);
      return;
    }

    // For other tasks, just claim/check
    onClaimTask(task.id);
  };

  const getTimeRemaining = (canClaimAt?: string) => {
    if (!canClaimAt) return '';
    const now = new Date();
    const claimTime = new Date(canClaimAt);
    const diff = claimTime.getTime() - now.getTime();
    
    if (diff <= 0) return '';
    
    const minutes = Math.floor(diff / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold text-white mb-2">
        {t('checkIn.tasks')}
      </h2>
      <p className="text-sm text-tg-hint mb-4">
        {t('checkIn.subtitle')}
      </p>
      
      {/* Task Category Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        <button
          onClick={() => onCategoryChange('special')}
          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
            taskCategory === 'special'
              ? 'bg-primary text-black shadow-lg shadow-primary/30'
              : 'bg-tg-secondary text-tg-hint border border-gray-800 hover:border-primary/50 hover:bg-[#2a2a2a] hover:scale-105'
          }`}
        >
          {t('checkIn.special')} ({tasksForCount.filter(t => t.category === 'special').length})
        </button>
        <button
          onClick={() => onCategoryChange('social')}
          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
            taskCategory === 'social'
              ? 'bg-primary text-black shadow-lg shadow-primary/30'
              : 'bg-tg-secondary text-tg-hint border border-gray-800 hover:border-primary/50 hover:bg-[#2a2a2a] hover:scale-105'
          }`}
        >
          {t('checkIn.social')} ({tasksForCount.filter(t => t.category === 'social').length})
        </button>
        <button
          onClick={() => onCategoryChange('trading')}
          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
            (taskCategory as string) === 'trading'
              ? 'bg-primary text-black shadow-lg shadow-primary/30'
              : 'bg-tg-secondary text-tg-hint border border-gray-800 hover:border-primary/50 hover:bg-[#2a2a2a] hover:scale-105'
          }`}
        >
          {t('checkIn.trading')} ({tasksForCount.filter(t => t.category === 'trading').length})
        </button>
      </div>

      {/* Tasks List - Sorted by: completion status → action type → priority */}
      <div className="space-y-3">
        {Object.entries(tasksByExchange).map(([exchange, exchangeTasks]) => (
          <div key={exchange} className='flex flex-col gap-4'>
            {exchange !== 'other' && (
              <div className="flex justify-between items-center mb-2 px-2">
                <div className="text-base font-semibold text-white uppercase">{exchange}</div>
                <div className="text-xs text-tg-hint">
                  {exchangeTasks.filter(t => t.status === 'available' || t.status === 'in_progress' || t.status === 'processing').length} {t('checkIn.available')}, {exchangeTasks.filter(t => t.status === 'claimed').length} {t('checkIn.completed')}
                </div>
              </div>
            )}
            {exchangeTasks.map((task) => {
              const timeRemaining = getTimeRemaining(task.canClaimAt);
              const canClaim = task.status === 'processing' && task.canClaimAt && new Date() >= new Date(task.canClaimAt);
              
              return (
                <div key={task.id} className="bg-tg-secondary rounded-xl p-4 flex items-start gap-4 border border-gray-800 hover:border-primary/50 hover:bg-[#2a2a2a] hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {(() => {
                      const icon = getActionIcon(task);
                      // Check if icon is a URL (starts with http:// or https://)
                      if (icon && (icon.startsWith('http://') || icon.startsWith('https://'))) {
                        return <img src={icon} alt="" className="w-full h-full object-cover" />;
                      }
                      // Otherwise, display as emoji/text
                      return <span className="text-2xl">{icon}</span>;
                    })()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-semibold text-white">
                        {task.title}
                      </h3>
                      {task.category === 'special' && <span className="text-xs">🔥</span>}
                    </div>
                    <p className="text-sm text-tg-hint mb-2">
                      {task.description}
                    </p>
                    
                    {/* Processing status with countdown */}
                    {task.status === 'processing' && timeRemaining && (
                      <div className="mb-2 px-3 py-2 bg-primary/10 rounded-lg border border-primary/30">
                        <div className="text-xs text-primary font-semibold mb-1">
                          {t('withdraw.processing')}
                        </div>
                        {!canClaim && (
                          <div className="text-xs text-tg-hint">
                            {timeRemaining}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Trading volume progress */}
                    {task.category === 'trading' && task.progress && (
                      <div className="mb-2">
                        <div className="flex justify-between text-xs text-tg-hint mb-1">
                          <span>
                            ${(task.progress.current / 1000).toFixed(0)}K / ${(task.progress.target / 1000000).toFixed(1)}M
                          </span>
                          <span>{task.progress.percentage.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-1.5">
                          <div
                            className="bg-primary h-1.5 rounded-full transition-all"
                            style={{ width: `${task.progress.percentage}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-primary">
                        {t('checkIn.points')}: +{formatPoints(task.points)}
                      </div>
                      {/* Show completion count if maxCompletions > 1 */}
                      {task.maxCompletions && task.maxCompletions > 1 && (
                        <div className="text-xs text-tg-hint bg-primary/10 px-2 py-1 rounded-lg border border-primary/30">
                          {task.completionCount || 0}/{task.maxCompletions} {t('checkIn.times')}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleTaskAction(task)}
                      disabled={task.status === 'claimed' || (task.status === 'processing' && !canClaim)}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                        task.status === 'claimed'
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                          : task.status === 'processing' && !canClaim
                          ? 'bg-yellow-500/20 text-yellow-400 cursor-not-allowed border border-yellow-500/30'
                          : 'bg-primary text-black hover:opacity-90 hover:scale-105 hover:shadow-lg hover:shadow-primary/30'
                      }`}
                    >
                      {getActionButtonText(task)}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Infinite scroll sentinel */}
      {onLoadMore && (
        <div id="tasks-scroll-sentinel" className="h-10 flex items-center justify-center">
          {loading && (
            <div className="text-tg-hint text-sm">
              {t('common.loading')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TasksSection;
